import {
  parsePredecessorLinks,
  parseSuccessorLinks,
  type ParsedPredecessorLink,
  type ParsedSuccessorLink,
  type EpcTaskRef,
} from './epcScheduleDependencies';

/** Lag segment like MS Project: `+15 days`, `-10 days`, or empty. */
export function formatLagSegment(lag: string | undefined): string {
  if (!lag) return '';
  const t = lag.trim();
  if (!t || t === '0' || t === '0d' || t === '0.0d') return '';
  if (/\d+\s*days?/i.test(t)) {
    return t.startsWith('+') || t.startsWith('-') ? t : `+${t}`;
  }
  const m = /^([+-]?[\d.]+)\s*d(ays?)?$/i.exec(t);
  if (m) {
    const n = parseFloat(m[1]!);
    if (n === 0) return '';
    const sign = n < 0 ? '-' : '+';
    const abs = Math.abs(n);
    const body = Number.isInteger(abs) ? String(abs) : String(abs);
    return `${sign}${body} days`;
  }
  if (/^[+-]/.test(t)) return t;
  return `+${t}`;
}

function idPart(uid: unknown): string {
  if (uid == null || uid === '') return '?';
  const n = Number(uid);
  if (Number.isFinite(n)) {
    return Number.isInteger(n) ? String(n) : String(n);
  }
  return String(uid);
}

/** One predecessor in MS style: `2SS+15 days`, `4SS`, `9SS+10 days`. Uses MS task id when present (matches Project UI). */
export function formatMsPredecessorLink(link: ParsedPredecessorLink): string {
  const id = idPart(link.predecessorTaskId ?? link.predecessorUniqueId);
  const code = (link.linkTypeCode || 'FS').toUpperCase();
  const lag = formatLagSegment(link.lag);
  return lag ? `${id}${code}${lag}` : `${id}${code}`;
}

/** One successor in MS style. */
export function formatMsSuccessorLink(link: ParsedSuccessorLink): string {
  const id = idPart(link.successorTaskId ?? link.successorUniqueId);
  const code = (link.linkTypeCode || 'FS').toUpperCase();
  const lag = formatLagSegment(link.lag);
  return lag ? `${id}${code}${lag}` : `${id}${code}`;
}

function firstStringField(task: EpcTaskRef, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = task[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

/**
 * Full predecessors column: prefer raw import string if present, else build from link rows.
 */
export function getPredecessorsMsColumnText(task: EpcTaskRef, _tasks: EpcTaskRef[]): string {
  const raw = firstStringField(task, [
    'predecessorsText',
    'predecessorText',
    'predecessorsString',
    'predecessorsRaw',
    'predecessorString',
  ]);
  if (raw) return raw;
  if (typeof task.predecessors === 'string' && task.predecessors.trim()) {
    return task.predecessors.trim();
  }
  const links = parsePredecessorLinks(task.predecessors);
  if (!links.length) return '';
  return links.map((l) => formatMsPredecessorLink(l)).join(', ');
}

export function getSuccessorsMsColumnText(task: EpcTaskRef, _tasks: EpcTaskRef[]): string {
  const raw = firstStringField(task, [
    'successorsText',
    'successorText',
    'successorsString',
    'successorsRaw',
    'successorString',
  ]);
  if (raw) return raw;
  if (typeof task.successors === 'string' && task.successors.trim()) {
    return task.successors.trim();
  }
  const links = parseSuccessorLinks(task.successors);
  if (!links.length) return '';
  return links.map((l) => formatMsSuccessorLink(l)).join(', ');
}

function parseResourceAssignments(raw: unknown): { resourceName: string; units?: string }[] {
  if (!Array.isArray(raw)) return [];
  const out: { resourceName: string; units?: string }[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const name = String(o.resourceName ?? '').trim();
    if (!name) continue;
    const u = o.units;
    out.push({
      resourceName: name,
      units: u != null && u !== '' ? String(u) : undefined,
    });
  }
  return out;
}

/**
 * Lines like `Land Contractor [10%]` — one row per resource.
 */
export function getResourceAssignmentsLines(task: EpcTaskRef): string[] {
  const raw = firstStringField(task, ['resourceAssignmentsText', 'resourcesText', 'resourceText']);
  if (raw) {
    return raw
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  const rows = parseResourceAssignments(task.resourceAssignments);
  if (rows.length) {
    return rows.map((r) => {
      const u = r.units ? ` [${r.units}]` : '';
      return `${r.resourceName}${u}`;
    });
  }
  const rn = task.resourceNames;
  if (typeof rn === 'string' && rn.trim()) {
    return rn
      .split(/[,;\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/** Split MS dependency string into segments (commas + newlines). */
export function splitMsDependencySegments(text: string): string[] {
  if (!text.trim()) return [];
  const parts: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    for (const seg of line.split(',')) {
      const s = seg.trim();
      if (s) parts.push(s);
    }
  }
  return parts;
}

/**
 * Parse one MS-style segment like `12FS+5 days` or `3SS` into id + link type + trailing lag text.
 * Returns null if the segment does not match the usual ID+type+lag pattern.
 */
export function parseMsDependencySegment(
  seg: string
): { idStr: string; linkType: string; lagTail: string } | null {
  const s = seg.trim();
  const m = /^(\d+|\?)(FS|SS|FF|SF)(.*)$/i.exec(s);
  if (!m) return null;
  return {
    idStr: m[1]!,
    linkType: m[2]!.toUpperCase(),
    lagTail: (m[3] ?? '').trim(),
  };
}
